/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import CampaignModel, {
  AudienceSettings,
  AudienceSettingsType,
  CampaignType,
  ObjectRelationshipTriggerType,
  ObjectTriggerType,
} from 'cio/models/campaign';
import { action } from '@ember/object';
import ObjectTypeModel from 'cio/models/object-type';
import { ConditionType } from 'cio/utils/condition-types';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import { PlanFeatures } from 'cio/models/account';

export interface CampaignsTriggerSetupAudienceArgs {
  campaign: CampaignModel;
  type: CampaignType;
  objectType?: ObjectTypeModel;
  audience?: CampaignModel['audience'];
  subType: ObjectTriggerType | ObjectRelationshipTriggerType;
  disabled: boolean;
  updateTopLevelValue: (key: 'audience', settings: CampaignsTriggerSetupAudienceArgs['audience']) => unknown;
}

export default class CampaignsTriggerSetupAudience extends Component<CampaignsTriggerSetupAudienceArgs> {
  @service auth!: AuthService;
  @service account!: AccountService;

  readonly conditionTypes = Object.freeze([ConditionType.Attribute, ConditionType.RelationshipAttribute]);

  get audienceOptions() {
    let { objectType, type, subType } = this.args;
    let { singularName } = objectType ?? { singularName: 'no-object' };
    let selfLabel = '';
    switch (type) {
      case CampaignType.Object: {
        switch (subType) {
          case ObjectTriggerType.Created: {
            selfLabel = `The person that is related to the created ${singularName.toLowerCase()}`;
            break;
          }
          case ObjectTriggerType.Updated: {
            selfLabel = `The person that is related to the updated ${singularName.toLowerCase()}`;
            break;
          }
          case ObjectTriggerType.Deleted: {
            selfLabel = `The person that is related to the deleted ${singularName.toLowerCase()}`;
            break;
          }
        }
        break;
      }
      case CampaignType.ObjectRelationship: {
        switch (subType) {
          case ObjectRelationshipTriggerType.Created: {
            selfLabel = `The person that was added to the ${singularName.toLowerCase()}`;
            break;
          }
          case ObjectRelationshipTriggerType.Updated: {
            selfLabel = `The person whose relationship to the ${singularName.toLowerCase()} is changed`;
            break;
          }
          case ObjectRelationshipTriggerType.Deleted: {
            selfLabel = `The person that was removed to the ${singularName.toLowerCase()}`;
            break;
          }
        }
        break;
      }
      case CampaignType.Behavioral: {
        selfLabel = 'The person who matches the segment conditions';
        break;
      }
      case CampaignType.Date: {
        selfLabel = 'The person who matches the date conditions';
        break;
      }
      case CampaignType.Transactional: {
        selfLabel = 'The person who performed the event';
        break;
      }
      case CampaignType.Form: {
        selfLabel = 'The person who submitted the event';
        break;
      }
    }
    let { disabled } = this.args;
    let options = [
      { label: selfLabel, value: AudienceSettingsType.self, disabled },
      { label: `Every person in the ${singularName.toLowerCase()}`, value: AudienceSettingsType.related, disabled },
      {
        label: `Certain people in the ${singularName.toLowerCase()}`,
        value: AudienceSettingsType.filtered,
        filters: true,
        disabled,
      },
    ];

    // Object campaign do not have "self" person, thus we need to disable this option
    if (type === CampaignType.Object) {
      return options.slice(1);
    }

    return options;
  }

  get selectedOption() {
    let { type } = this.args.audience ?? {};
    return this.audienceOptions.find((opt) => opt.value === type);
  }

  get conditions() {
    let { audience } = this.args;
    return [...(audience?.personFilters ?? []), ...(audience?.relationshipFilters ?? [])];
  }

  get hasReadOnlyPermission() {
    if (this.args.campaign.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  get conditionLabelOverrides() {
    return {
      [ConditionType.Attribute]: `Person`,
      [ConditionType.RelationshipAttribute]: `Relationship`,
    };
  }

  get showLimitBanner() {
    return (
      this.audiencePeopleLimit !== false &&
      AudienceSettingsType.related === this.args.audience?.type &&
      this.audiencePeopleLimit > 0
    );
  }

  get audiencePeopleLimit() {
    return this.account.planFeatures[PlanFeatures.MaxObjectJourneys]?.limit;
  }

  get placeholder() {
    if (this.args.disabled) {
      return this.audienceOptions[0].label;
    }
    return 'Choose audience';
  }

  get disabledTooltip() {
    let triggerType: string = this.args.type;
    if (triggerType === CampaignType.Behavioral) {
      triggerType = 'segment';
    }
    return `For now, campaigns with ${triggerType} triggers cannot change the audience. You can use <a href="https://customer.io/docs/journeys/object-and-relationship-campaigns/#trigger-campaigns-based-on-objects-or-relationships" target="_blank" rel="noopener noreferrer">custom object triggers</a> to customize the audience.`;
  }

  @action
  setAudienceType({ value }: { value: AudienceSettingsType }) {
    if (this.args.disabled || value === this.args.audience?.type) {
      return;
    }
    this.args.updateTopLevelValue('audience', {
      type: value,
      personFilters: value === AudienceSettingsType.filtered ? this.args.campaign.audience?.personFilters ?? [] : [],
      relationshipFilters:
        value === AudienceSettingsType.filtered ? this.args.campaign.audience?.relationshipFilters ?? [] : [],
    });
  }

  @action
  setAudienceFilters(filters: AudienceSettings['personFilters'] & AudienceSettings['relationshipFilters']) {
    if (this.args.disabled) {
      return;
    }
    let personFilters: AudienceSettings['personFilters'] = [];
    let relationshipFilters: AudienceSettings['relationshipFilters'] = [];
    // filter and sort out relationshipFilters in single loop
    for (let f of filters) {
      if (f.type === ConditionType.Attribute) {
        personFilters.push(f);
      } else {
        relationshipFilters.push(f);
      }
    }
    this.args.updateTopLevelValue('audience', {
      type: this.args.audience?.type ?? AudienceSettingsType.filtered,
      personFilters,
      relationshipFilters,
    });
  }
}
